/* eslint-disable @typescript-eslint/no-unsafe-argument */

import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  usePostManuscriptsOutputErrorList,
  usePostManuscriptsOutputFrame,
  usePostManuscriptsOutputIndex,
  usePostManuscriptsOutputReference,
} from '../../../../api/manuscripts/manuscripts';
import { usePostUtilsDownload } from '../../../../api/utils/utils';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import '../../../../styles/Dropdown.css';
import { executeDownload } from '../../../../utils/download';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';

interface Props {
  productionId: number;
  isReadOnly: boolean;
}

/* 原稿タブ出力系プルダウン */
function ManuscriptsTabOutputCommand({ productionId, isReadOnly }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const optionsForBinary = useOptionsForBinary(language);
  const defaultOptions = useDefaultOptions(language);

  // #region UI制御
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownListRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const handleClickToCloseDropdown = (event: any) => {
      const element = dropdownListRef.current;
      if (!showDropdown || element?.contains(event.target)) return;
      setShowDropdown(false);
    };
    window.addEventListener('click', handleClickToCloseDropdown);
    return () => {
      window.removeEventListener('click', handleClickToCloseDropdown);
    };
  }, [showDropdown, dropdownListRef]);
  // #endregion

  // #region 原稿エラー
  // 定義
  const [dispOutputErrorListError, setDispOutputErrorListError] = useState(false);
  const {
    mutateAsync: outputErrorListAsync,
    error: outputErrorListError,
    isLoading: outputErrorListIsloading,
  } = usePostManuscriptsOutputErrorList<AxiosError<Blob>>(defaultOptions);

  useEffect(() => {
    setDispOutputErrorListError(outputErrorListError != null);
  }, [outputErrorListError]);

  // ダウンロードAPI
  const {
    mutateAsync: _downloadAsync,
    isError: downloadIsError,
    error: downloadError,
  } = usePostUtilsDownload<AxiosError<Blob>>(optionsForBinary);

  // ダウンロードエラーの表示
  const [displayDownloadError, setDisplayDownloadError] = useState(false);
  useEffect(() => {
    setDisplayDownloadError(downloadIsError);
  }, [downloadIsError]);
  const [resultMessge, setResultMessage] = useState<string | undefined>('');

  // ダウンロード処理
  const onOutputErrorListClicked = useCallback(async () => {
    const response = await outputErrorListAsync({ productionId, data: { section_id: undefined } });
    if (response.status === 200) {
      // 処理が成功したら結果をダウンロード
      console.log(response);
      if (response.data.result_file_info !== undefined && response.data.result_file_info !== null) {
        console.log(response.data.result_file_info);
        await _downloadAsync(
          { data: response.data.result_file_info },
          {
            onSuccess: (responseDL) => executeDownload(responseDL),
          }
        );
      } else {
        console.log(response.data.message);
        setResultMessage(response.data.message);
      }
    }
  }, [productionId, outputErrorListAsync, _downloadAsync]);
  // #endregion

  // #region 参照リスト
  // 定義
  const [dispOutputReferError, setDispOutputReferError] = useState(false);
  const {
    mutateAsync: outputReferAsync,
    error: outputReferError,
    isLoading: outputReferIsloading,
  } = usePostManuscriptsOutputReference<AxiosError<Blob>>(optionsForBinary);

  useEffect(() => {
    setDispOutputReferError(outputReferError != null);
  }, [outputReferError]);

  // ダウンロード処理
  const onOutputReferClicked = useCallback(async () => {
    const response = await outputReferAsync({ productionId });
    if (response.status === 200) {
      executeDownload(response);
    }
  }, [productionId, outputReferAsync]);
  // #endregion

  // #region 索引リスト
  // 定義
  const [dispOutputIndexError, setDispOutputIndexError] = useState(false);
  const {
    mutateAsync: outputIndexAsync,
    error: outputIndexError,
    isLoading: outputIndexIsloading,
  } = usePostManuscriptsOutputIndex<AxiosError<Blob>>(optionsForBinary);

  useEffect(() => {
    setDispOutputIndexError(outputIndexError != null);
  }, [outputIndexError]);

  // ダウンロード処理
  const onOutputIndexClicked = useCallback(async () => {
    const response = await outputIndexAsync({ productionId });
    if (response.status === 200) {
      executeDownload(response);
    }
  }, [productionId, outputIndexAsync]);
  // #endregion

  // #region FrameMaker用XMLダウンロード

  // 定義
  const [dispConfiOutputFm, setDispConfiOutputFm] = useState(false);
  const [dispOutputFmErr, setDispOutputFmErr] = useState(false);
  const {
    mutateAsync: outputFmAsync,
    error: outputFmErr,
    isLoading: outputEmIsloading,
  } = usePostManuscriptsOutputFrame<AxiosError<Blob>>(optionsForBinary);

  useEffect(() => {
    setDispOutputFmErr(outputFmErr != null);
  }, [outputFmErr]);

  // ダウンロード処理
  const onOutputFm = useCallback(async () => {
    const response = await outputFmAsync({ productionId });
    if (response.status === 200) {
      executeDownload(response);
    }
  }, [productionId, outputFmAsync]);
  // #endregion

  return (
    <div className="dropdowncontainer">
      <span
        className="dropdownopen"
        onClick={() => !isReadOnly && setShowDropdown((prevState) => !prevState)}
        ref={dropdownListRef}
        style={{
          width: '100px',
          color: isReadOnly ? '#9d9d9d' : '',
          cursor: isReadOnly ? 'default' : 'pointer',
        }}
      >
        <i
          className="fa-solid fa-download"
          style={{
            color: isReadOnly ? '#9d9d9d' : '',
            cursor: isReadOnly ? 'default' : 'pointer',
          }}
        />
        {t('各種情報出力')}
      </span>
      {showDropdown && (
        <ul className="dropdownlist" style={{ width: '230px' }}>
          <li className="dropdownitem" onClick={() => onOutputErrorListClicked()}>
            {t('原稿エラー一覧')}
          </li>
          <li className="dropdownitem" onClick={() => onOutputReferClicked()}>
            {t('リンク情報出力')}
          </li>
          <li className="dropdownitem" onClick={() => onOutputIndexClicked()}>
            {t('索引情報出力')}
          </li>
          {/* 
          <li className="dropdownitem" onClick={() => setDispConfiOutputFm(true)}>
            {t('FrameMaker組版用XML')}
          </li>
          */}
        </ul>
      )}
      {/* スピナーモーダル */}
      {(outputEmIsloading || outputReferIsloading || outputIndexIsloading || outputErrorListIsloading) && (
        <SpinnerModal />
      )}
      {/* リンクダウンロードエラー */}
      {dispOutputReferError && (
        <BlobErrorModal error={outputReferError} onClickOk={() => setDispOutputReferError(false)} />
      )}
      {/* 索引ダウンロードエラー */}
      {dispOutputIndexError && (
        <BlobErrorModal error={outputIndexError} onClickOk={() => setDispOutputIndexError(false)} />
      )}
      {/* FrameMaker用XML出力確認メッセージ */}
      {dispConfiOutputFm && (
        <ConfirmModal
          onClickCancel={() => setDispConfiOutputFm(false)}
          onClickOk={() => {
            void onOutputFm();
            setDispConfiOutputFm(false);
          }}
        >
          <div>{t('FrameMaker用XMLを出力しますか？')}</div>
        </ConfirmModal>
      )}
      {/* FrameMaker用XMLエラー */}
      {dispOutputFmErr && <BlobErrorModal onClickOk={() => setDispOutputFmErr(false)} error={outputFmErr} />}
      {/* 原稿エラー一覧エラー */}
      {dispOutputErrorListError && (
        <BlobErrorModal error={outputErrorListError} onClickOk={() => setDispOutputErrorListError(false)} />
      )}
      {/* ダウンロード処理エラー */}
      {displayDownloadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownloadError(false)} />
      )}
      {resultMessge !== '' && (
        <OkModal
          onClickOk={() => {
            setResultMessage('');
          }}
        >
          <div>{resultMessge}</div>
        </OkModal>
      )}
    </div>
  );
}
export default ManuscriptsTabOutputCommand;
